// Decathlon Scoring (118) -- athletes x 10 sub-events grid; cumulative IAAF
// points are the headline. The total + placing update as results are entered.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { DecathlonResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Trophy } from "lucide-react";

export default function DecathlonMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DecathlonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  const load = useCallback(() => {
    if (!id) return;
    api.getDecathlonScore(id).then(setData)
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => load(), [load]);

  async function addAthlete() {
    if (!id || !name.trim()) return;
    setData(await api.addDecathlonAthlete(id, { athlete_name: name.trim() }));
    setName("");
  }
  async function setResult(athleteId: string, discipline: string, value: string) {
    if (value.trim() === "" || Number.isNaN(Number(value))) return;
    setData(await api.recordDecathlonResult(athleteId, { discipline, performance: Number(value) }));
  }

  if (loading) return <div className="h-40 animate-pulse rounded-lg bg-muted" data-page="decathlon-score" data-state="loading" />;
  if (error || !data) return <p className="text-sm text-destructive" data-page="decathlon-score" data-error>{error ?? "Not found"}</p>;

  const canManage = data._actions.can_manage;

  return (
    <div className="flex flex-col gap-4" data-page="decathlon-score" data-state="ready">
      <Link to={`/events/${data.event_id}`} className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <div className="flex items-center gap-2">
        <Trophy className="h-5 w-5 text-primary" aria-hidden />
        <h1 className="text-xl font-semibold">{data.match_name} <span className="text-sm font-normal text-muted-foreground">- Decathlon</span></h1>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Athlete</th>
              {data.disciplines.map((d) => (
                <th key={d.key} className="px-2 py-2 text-center whitespace-nowrap" title={`${d.label} (${d.unit})`}>{d.label}</th>
              ))}
              <th className="px-3 py-2 text-right font-semibold">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {data.athletes.map((a) => (
              <tr key={a.id} data-athlete={a.id} className={a.dq ? "opacity-60" : ""}>
                <td className="px-3 py-2 font-semibold tabular-nums">{a.dq ? "DQ" : a.placing ?? "-"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{a.athlete_name}{a.school_name && <span className="ml-1 text-xs text-muted-foreground">{a.school_name}</span>}</td>
                {data.disciplines.map((d) => {
                  const ev = a.events.find((e) => e.discipline === d.key);
                  return (
                    <td key={d.key} className="px-2 py-1.5 text-center" data-event={d.key}>
                      {canManage ? (
                        <input
                          defaultValue={ev?.performance ?? ""}
                          onBlur={(e) => setResult(a.id, d.key, e.target.value)}
                          placeholder="--"
                          className="w-14 rounded border border-input bg-background px-1 py-0.5 text-center text-xs"
                          data-field="performance"
                        />
                      ) : (ev?.performance ?? "-")}
                      <div className="text-[10px] text-muted-foreground tabular-nums">{ev?.points ?? 0}</div>
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right text-base font-semibold tabular-nums" data-field="total">{a.total_points}</td>
              </tr>
            ))}
            {data.athletes.length === 0 && (
              <tr><td colSpan={data.disciplines.length + 3} className="px-3 py-4 text-center text-xs text-muted-foreground">No athletes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {canManage && (
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add athlete..." className="rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-field="athlete-name" />
          <Button size="sm" variant="outline" onClick={addAthlete} disabled={!name.trim()} data-action="add-athlete"><Plus className="mr-1 h-4 w-4" /> Add athlete</Button>
        </div>
      )}
      <p className="text-xs text-muted-foreground">Enter performances in each event's native unit (runs in seconds, jumps in cm, throws in metres). Points are the IAAF total.</p>
    </div>
  );
}
